import { useState, useMemo, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import TextInput from "../components/ui/Input/TextInput";
import useFetch from "../hooks/useFetch";
import { useCurrentStore } from "../hooks/useCurrentStore";
import styles from "../components/EntityAddUpdateForm/EntityAddUpdateForm.module.css";
import DashboardSidebar from "../components/DashboardSidebar";

const EditProfile: React.FC = () => {

  const { store_id } = useParams<{ store_id: string }>();
  const location = useLocation();
  const isEditStoreRoute = !!store_id;

  const isEditStoreOwnerRoute = location.pathname.startsWith("/edit-store-owner/");
  const isEditStoreOnlyRoute = location.pathname.startsWith("/edit-store/") && !isEditStoreOwnerRoute;

  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);
  const currentRole = storedUser?.current_role;
  const currentStoreId = useCurrentStore();
  const { data: userData, fetchData: fetchUserData, errorContent, hasFetched} = useFetch({
    url: `${import.meta.env.VITE_API_URL}/users/0`,
    method: "GET",
  });

  useEffect(() => {
    fetchUserData();
  }, [location.pathname]);
  const store = storedUser?.stores?.find((s: any) => String(s.id) === currentStoreId) ?? storedUser?.stores?.[0];
  const storeId = isEditStoreRoute ? store_id : store?.id;


  const defaultFullName = isEditStoreRoute
    ? `${location.state?.owner_first_name || ""} ${location.state?.owner_last_name || ""}`.trim()
    : `${storedUser?.first_name || ""} ${storedUser?.last_name || ""}`.trim();

  const [fullName, setFullName] = useState<string>(defaultFullName);

  const [ownerEmail, setOwnerEmail] = useState<string>(
    isEditStoreRoute ? (location.state?.owner_email || "") : (storedUser?.email || "")
  );
  const [ownerAddress, setOwnerAddress] = useState<string>(
    isEditStoreRoute ? (location.state?.owner_address || "") : (storedUser?.address || "")
  );
  const [password, setPassword] = useState<string>("");
  const [storeName, setStoreName] = useState<string>(
    isEditStoreRoute ? (location.state?.storeName || "") : (store?.name || "")
  );
  const [studioOrgName, setStudioOrgName] = useState<string>(
    isEditStoreRoute ? (location.state?.studio_org_name || "") : (store?.studio_organization_name || "")
  );

  const [storeDescription, setStoreDescription] = useState<string>(
    isEditStoreRoute ? (location.state?.store_description || "") : (store?.description || "")
  );

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

const seedFormFromStorage = (useCurrentStore = false) => {
  const latestUser = isEditStoreOwnerRoute
    ? {
        first_name: location.state?.owner_first_name || "",
        last_name: location.state?.owner_last_name || "",
        email: location.state?.owner_email || "",
        address: location.state?.owner_address || "",
      }
    : isEditStoreOnlyRoute
    ? {
        stores: [{ 
          id: store_id,
          name: location.state?.storeName || "",
          studio_organization_name: location.state?.studio_org_name || "",
          description: location.state?.store_description || "",
        }]
      }
    : (userData?.data ?? userData ?? {});
  const currentStoreId = localStorage.getItem("currentStoreId");
  const currentStore = latestUser?.stores?.find((s: any) => String(s.id) === currentStoreId) ?? latestUser?.stores?.[0];

  if (!isEditStoreRoute || useCurrentStore) {
    // Edit profile route OR store was switched — always use the currently selected store
    setFullName(`${latestUser?.first_name || ""} ${latestUser?.last_name || ""}`.trim());
    setOwnerEmail(latestUser?.email || "");
    setOwnerAddress(latestUser?.address || "");
    setStoreName(currentStore?.name || "");
    setStudioOrgName(currentStore?.studio_organization_name || "");
    const lsUser = (() => { try { return JSON.parse(localStorage.getItem("user") || "{}"); } catch { return {}; } })();
    const lsStore = lsUser?.stores?.find((s: any) => String(s.id) === String(store_id));
    setStoreDescription(lsStore?.description || currentStore?.description || "");
  } else {
    // /edit-store/:id opened fresh — read that specific store from localStorage
    const editedStore = latestUser?.stores?.find((s: any) => String(s.id) === String(store_id));
    setStoreName(editedStore?.name || location.state?.storeName || "");
    setStudioOrgName(editedStore?.studio_organization_name || location.state?.studio_org_name || "");
    setStoreDescription(editedStore?.description || location.state?.store_description || "");
  }
};

useEffect(() => {
  // Reset first
  setFullName("");
  setOwnerEmail("");
  setOwnerAddress("");
  setStoreName("");
  setStudioOrgName("");
  setStoreDescription("");

  if (isEditStoreOwnerRoute) {
    setFullName(`${location.state?.owner_first_name || ""} ${location.state?.owner_last_name || ""}`.trim());
    setOwnerEmail(location.state?.owner_email || "");
    setOwnerAddress(location.state?.owner_address || "");
  } else if (isEditStoreOnlyRoute) {
    setStoreName(location.state?.storeName || "");
    setStudioOrgName(location.state?.studio_org_name || "");
    setStoreDescription(location.state?.store_description || "");
  }
}, [location.pathname, location.state, store_id]);

useEffect(() => {
  if (!userData || isEditStoreOwnerRoute || isEditStoreOnlyRoute) return;
  seedFormFromStorage(false);
}, [userData]);

useEffect(() => {
  if (!isEditStoreOwnerRoute && !isEditStoreOnlyRoute) {
    fetchUserData();
  }
}, [location.pathname]);

useEffect(() => {
  if (isEditStoreOnlyRoute) return;
  if (!isEditStoreOwnerRoute && !userData) return;
  seedFormFromStorage(true);
}, [currentStoreId]);

  const { fetchData, isLoading: loading } = useFetch({
    url: isEditStoreOwnerRoute
    ? `${import.meta.env.VITE_API_URL}/stores/update-store-profile/${storeId}`
    : isEditStoreOnlyRoute
      ? `${import.meta.env.VITE_API_URL}/stores/update-store-profile/${storeId}`
      : `${import.meta.env.VITE_API_URL}/users/update/${storedUser?.id}`,
    method: "PUT",
  });

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);
    
    
    const storedUser = (() => { try { return JSON.parse(localStorage.getItem("user") || "{}"); } catch { return {}; } })();
    
    const payload: Record<string, any> = {};
    const spaceIndex = fullName.indexOf(" ");
    const firstName = spaceIndex !== -1 ? fullName.slice(0, spaceIndex) : fullName;
    const lastName = spaceIndex !== -1 ? fullName.slice(spaceIndex + 1) : "";
    if (firstName.trim()) payload.first_name = firstName;
    if (lastName.trim()) payload.last_name = lastName;
    if (ownerEmail.trim()) payload.email = ownerEmail;
    if (ownerAddress.trim()) payload.address = ownerAddress;
    
    if (password.trim()) payload.password = password;
      
    if ((currentRole !== 1 || isEditStoreRoute) && storeName.trim()) payload.name = storeName;
    if ((currentRole !== 1 || isEditStoreRoute) && storeDescription.trim()) payload.description = storeDescription;
    if ((currentRole !== 1 || isEditStoreRoute) && studioOrgName.trim()) payload.studio_organization_name = studioOrgName;

    if (Object.keys(payload).length === 0) {
      setErrorMessage("Nothing to update.");
      return;
    }

    const result = await fetchData(payload);

    if (result?.success) {
      setSuccessMessage("Details updated successfully!");
      const updated = { ...storedUser };
      if (firstName.trim()) updated.first_name = firstName;
      if (lastName.trim()) updated.last_name = lastName;
      if (ownerAddress.trim()) updated.address = ownerAddress;
      const activeStoreId = isEditStoreRoute ? store_id : localStorage.getItem("currentStoreId");
      const storeIdx = updated.stores?.findIndex((s: any) => String(s.id) === String(activeStoreId));
      if (storeIdx !== -1 && updated.stores?.[storeIdx]) {
        if (isEditStoreRoute || currentRole !== 1) {
          if (storeName.trim()) updated.stores[storeIdx].name = storeName;
          if (studioOrgName.trim()) updated.stores[storeIdx].studio_organization_name = studioOrgName;
          if (storeDescription.trim()) updated.stores[storeIdx].description = storeDescription;
        }
      }

      localStorage.setItem("user", JSON.stringify(updated));
      window.dispatchEvent(new Event("user-updated"));
      window.dispatchEvent(new Event("store-changed"));
    } else {
      const raw = result?.errors?.length
        ? result.errors.map((e: any) => Object.values(e).join(", ")).join(", ")
        : result?.message || "Failed to update.";
      setErrorMessage(raw.replace(/^Error in \w+:\s*/i, ""));
    }
  };

  if (hasFetched && errorContent) {
    return (
      <div className="page-wrapper-columnwise">
        <div className="content-columnwise">
          <div className="center-container">{errorContent}</div>
        </div>
      </div>
    );
  }

  return (
  <div className="page-wrapper-columnwise">
    {(currentRole !== 1) && <DashboardSidebar/>}
        
    <div className="content-columnwise">
    <div className="center-container">
      <form onSubmit={handleUpdate} className={styles.entityChangePasswordForm}>
        <h2 className="form-title">Update Details</h2>
        <h5>{!isEditStoreOnlyRoute ?
        "Edit Profile":
        "Edit Store Details"
        }</h5>

        {!isEditStoreOnlyRoute && (
        <>
        <TextInput
          label="Full Name"
          placeholder="Full Name"
          value={fullName}
          onChange={(e) => { setErrorMessage(null); setSuccessMessage(null); setFullName(e.target.value); }}
        />
        <TextInput
          label="Email"
          placeholder="Email"
          type="email"
          value={ownerEmail}
          onChange={(e) => { setErrorMessage(null); setSuccessMessage(null); setOwnerEmail(e.target.value); }}
        />
        <TextInput
          label="Address"
          placeholder="Address"
          value={ownerAddress}
          onChange={(e) => { setErrorMessage(null); setSuccessMessage(null); setOwnerAddress(e.target.value); }}
        />

        <TextInput
          label="Password"
          type="password"
          placeholder="New Password"
          value={password}
          onChange={(e) => { setErrorMessage(null); setSuccessMessage(null); setPassword(e.target.value); }}
        />
        </>)}

        {(isEditStoreOnlyRoute) && (

          <>
            <TextInput
              label="Store Name"
              placeholder="Store Name"
              value={storeName}
              onChange={(e) => { setErrorMessage(null); setSuccessMessage(null); setStoreName(e.target.value); }}
            />
            <TextInput
              label="Studio Organization Name"
              placeholder="Studio Organization Name"
              value={studioOrgName}
              onChange={(e) => { setErrorMessage(null); setSuccessMessage(null); setStudioOrgName(e.target.value); }}
            />
            <div>
              <label className="field-label">Description</label>
              <textarea
                className="text-area-field w-input"
                placeholder="Store Description"
                maxLength={255}
                rows={4}
                value={storeDescription}
                onChange={(e) => { setErrorMessage(null); setSuccessMessage(null); setStoreDescription(e.target.value); }}
              />
            </div>
          </>
          
        )}

        {successMessage && <p className="success-message">{successMessage}</p>}
        {errorMessage && <p className="error-message">{errorMessage}</p>}

        <div className={styles.buttonContainer}>
          <button type="submit" className={styles.entityAddUpdateForm__submitbtn} disabled={loading}>
            {loading ? "Updating..." : "Update Details"}
          </button>
        </div>
      </form>
    </div>
    </div>
  </div>

  );
};

export default EditProfile;